import React, { useState, useRef } from 'react';
import { Archive, Search, Filter, Download, Eye, FileText, Calendar, User, DollarSign, Tag, Grid, List, X, RefreshCw } from 'lucide-react';
import { formatDZD } from '../utils/currency';
import { downloadDocumentPDF, generateWithScreenshot } from '../utils/pdfGenerator';
import { useDocuments } from '../contexts/DocumentsContext';
import { useSettings } from '../contexts/SettingsContext';
import { useClients } from '../contexts/ClientsContext';
import DocumentPreview from './DocumentPreview';
import { resetDocuments } from '../utils/resetDocuments';
import './Archives.css';

const Archives = () => {
  const { documents } = useDocuments();
  const { settings } = useSettings();
  const { clients } = useClients();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  
  // Preview modal state
  const [previewDocument, setPreviewDocument] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  // Download status
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  // Document to be exported as PDF
  const [documentToPrint, setDocumentToPrint] = useState(null);

  const documentTypes = {
    invoice: { label: 'Facture', icon: FileText, color: '#3b82f6' },
    proforma: { label: 'Facture Proforma', icon: FileText, color: '#8b5cf6' },
    quote: { label: 'Devis', icon: FileText, color: '#10b981' },
    quote_request: { label: 'Demande de Devis', icon: FileText, color: '#f59e0b' },
    purchase_order: { label: 'Bon de Commande', icon: FileText, color: '#ef4444' },
    delivery: { label: 'Bon de Livraison', icon: FileText, color: '#06b6d4' },
    shipping: { label: 'Bordereau d\'Expédition', icon: FileText, color: '#84cc16' },
    material_exit: { label: 'Bon de Sortie Matériel', icon: FileText, color: '#f59e0b' },
    reception_note: { label: 'Bon de Réception', icon: FileText, color: '#10b981' },
    return_slip: { label: 'Bordereau de Retour', icon: FileText, color: '#dc2626' },
    attachment: { label: 'Attachement', icon: FileText, color: '#8b5cf6' },
    // Add a default entry for any undefined document types
    default: { label: 'Document', icon: FileText, color: '#64748b' }
  };

  const statusConfig = {
    paid: { label: 'Payée', color: '#10b981' },
    pending: { label: 'En attente', color: '#f59e0b' },
    overdue: { label: 'En retard', color: '#ef4444' },
    draft: { label: 'Brouillon', color: '#64748b' },
    accepted: { label: 'Accepté', color: '#10b981' },
    rejected: { label: 'Refusé', color: '#ef4444' },
    confirmed: { label: 'Confirmé', color: '#3b82f6' },
    delivered: { label: 'Livré', color: '#10b981' },
    shipped: { label: 'Expédié', color: '#06b6d4' }
  };

  const categories = ['FACTURATION', 'OFFRES COMMERCIALES', 'LOGISTIQUE'];

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.client.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || doc.type === filterType;
    const matchesCategory = filterCategory === 'all' || doc.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || doc.status === filterStatus;
    
    let matchesDate = true;
    if (dateRange.start && dateRange.end) {
      matchesDate = doc.date >= dateRange.start && doc.date <= dateRange.end;
    }
    
    return matchesSearch && matchesType && matchesCategory && matchesStatus && matchesDate;
  });

  // Helper function to convert document types
  const convertDocumentType = (type) => {
    const typeMap = {
      'invoice': 'invoice',
      'proforma': 'proforma',
      'quote': 'quote',
      'quote_request': 'quote-request',
      'purchase_order': 'purchase-order',
      'delivery': 'delivery-note',
      'shipping': 'shipping-slip',
      'material_exit': 'material-exit',
      'reception_note': 'reception-note',
      'return_slip': 'return-slip',
      'attachment': 'attachment'
    };
    return typeMap[type] || type;
  };

  const handleGeneratePDF = async (document) => {
    try {
      setIsGeneratingPDF(true);
      
      // Find client data for this document
      const clientData = clients.find(client => client.name === document.client) || null;
      
      // Convert the document type
      const convertedType = convertDocumentType(document.type);
      
      // Set the document for the hidden DocumentPreview component
      setDocumentToPrint({
        document: document,
        client: clientData,
        type: convertedType
      });
      
      // Use a timeout to allow the DocumentPreview component to render
      setTimeout(async () => {
        try {
          // Use the direct downloadDocumentPDF function which works reliably
          await downloadDocumentPDF(convertedType, document, clientData, settings);
          
          setTimeout(() => {
            setIsGeneratingPDF(false);
            setDocumentToPrint(null);
          }, 500);
        } catch (error) {
          console.error('Error generating PDF:', error);
          setIsGeneratingPDF(false);
          setDocumentToPrint(null);
          alert('Une erreur est survenue lors du téléchargement du document.');
        }
      }, 600);
    } catch (error) {
      console.error('Error preparing PDF generation:', error);
      setIsGeneratingPDF(false);
      setDocumentToPrint(null);
      alert('Une erreur est survenue lors de la préparation du document.');
    }
  };

  const handleView = (document) => {
    // Set the document to preview and show modal
    setPreviewDocument(document);
    setShowPreviewModal(true);
  };

  const handleResetDocuments = () => {
    if (window.confirm('Êtes-vous sûr de vouloir réinitialiser les documents ? Cette action supprimera tous les documents actuels et ne laissera que 5 documents.')) {
      resetDocuments();
      window.location.reload(); // Reload the page to reflect changes
    }
  };

  const stats = {
    total: documents.length,
    invoices: documents.filter(d => d.type === 'invoice').length,
    quotes: documents.filter(d => d.type === 'quote').length,
    totalAmount: documents.reduce((sum, d) => sum + (d.amount || 0), 0),
    thisMonth: documents.filter(d => {
      const docDate = new Date(d.date);
      const now = new Date();
      return docDate.getMonth() === now.getMonth() && docDate.getFullYear() === now.getFullYear();
    }).length
  };

  return (
    <div className="archives fade-in">
      {/* Hidden Document Preview for PDF generation */}
      {documentToPrint && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <DocumentPreview 
            documentType={documentToPrint.type}
            formData={documentToPrint.document}
            clientData={documentToPrint.client}
            isRealTimePreview={true}
            onSave={() => {}} // Dummy function since we're not saving
            onBack={() => {}} // Dummy function since we're not using back
          />
        </div>
      )}
      
      <div className="page-header">
        <h1 className="page-title" style={{ color: '#ffffff' }}>
          <Archive size={32} />
          Archives & Documents
        </h1>
        <p className="page-subtitle" style={{ color: '#ffffff' }}>Consultez, recherchez et téléchargez tous vos documents</p>
      </div>

      {/* Statistics */}
      <div className="archives-stats">
        <div className="stat-item">
          <div className="stat-icon" style={{background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)"}}>
            <Archive size={24} />
          </div>
          <span className="stat-number">{stats.total}</span>
          <span className="stat-label">DOCUMENTS TOTAL</span>
        </div>

        <div className="stat-item">
          <div className="stat-icon invoices">
            <FileText size={24} />
          </div>
          <span className="stat-number">{stats.invoices}</span>
          <span className="stat-label">FACTURES</span>
        </div>

        <div className="stat-item">
          <div className="stat-icon quotes">
            <FileText size={24} />
          </div>
          <span className="stat-number">{stats.quotes}</span>
          <span className="stat-label">DEVIS</span>
        </div>

        <div className="stat-item amount-item">
          <div className="stat-icon amount">
            <DollarSign size={24} />
          </div>
          <span className="stat-number">
            {new Intl.NumberFormat('fr-DZ', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(stats.totalAmount)} DA
          </span>
          <span className="stat-label">MONTANT TOTAL</span>
        </div>

        <div className="stat-item">
          <div className="stat-icon month">
            <Calendar size={24} />
          </div>
          <span className="stat-number">{stats.thisMonth}</span>
          <span className="stat-label">CE MOIS-CI</span>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="archives-toolbar">
        <div className="search-filters">
          <div className="search-input">
            <Search size={20} />
            <input
              type="text"
              placeholder="Rechercher un document..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tous types</option>
            {Object.entries(documentTypes).map(([key, type]) => (
              <option key={key} value={key}>{type.label}</option>
            ))}
          </select>

          <select 
            value={filterCategory} 
            onChange={(e) => setFilterCategory(e.target.value)}
            className="filter-select"
          >
            <option value="all">Toutes catégories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tous statuts</option>
            {Object.entries(statusConfig).map(([key, status]) => (
              <option key={key} value={key}>{status.label}</option>
            ))}
          </select>
        </div>

        <div className="toolbar-right">
          <div className="view-toggle">
            <button
              className={`toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Vue cartes"
            >
              <Grid size={18} />
            </button>
            <button
              className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Vue tableau"
            >
              <List size={18} />
            </button>
          </div>

          <div className="date-filters">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
              className="date-input"
            />
            <span>à</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
              className="date-input"
            />
          </div>
        </div>
      </div>

      {/* Documents Display */}
      {viewMode === 'table' ? (
        <div className="documents-table-container">
          <div className="table-header">
            <div>Type</div>
            <div>Numéro</div>
            <div>Titre</div>
            <div>Client</div>
            <div>Produits/Services</div>
            <div>Date</div>
            <div>Montant</div>
            <div>Statut</div>
            <div>Actions</div>
          </div>
          
          {filteredDocuments.map(document => {
            // Safely get document type or use default if type doesn't exist
            const docType = documentTypes[document.type] || documentTypes.default;
            // Safely get status or use a default
            const docStatus = statusConfig[document.status] || { label: 'Statut', color: '#64748b' };
            const IconComponent = docType.icon;

            // Get product information
            const productInfo = document.items && document.items.length > 0 
              ? (
                <div className="product-names">
                  {document.items.slice(0, 2).map((item, index) => (
                    <span key={item.id} className="product-name">
                      {item.name}
                    </span>
                  ))}
                  {document.items.length > 2 && (
                    <span className="more-products">+{document.items.length - 2} autres</span>
                  )}
                </div>
              ) 
              : '-';

            return (
              <div key={document.id} className="table-row">
                <div className="table-cell" data-label="Type">
                  <div className="document-type-cell">
                    <div className="type-icon" style={{ backgroundColor: docType.color }}>
                      <IconComponent size={16} />
                    </div>
                    <span>{docType.label}</span>
                  </div>
                </div>
                <div className="table-cell document-number" data-label="Numéro">{document.number}</div>
                <div className="table-cell" data-label="Titre">{document.title}</div>
                <div className="table-cell" data-label="Client">
                  <div className="client-info">
                    <User size={14} />
                    <span>{document.client}</span>
                  </div>
                </div>
                <div className="table-cell" data-label="Produits">
                  <div className="products-info">
                    {productInfo}
                  </div>
                </div>
                <div className="table-cell" data-label="Date">
                  {new Date(document.date).toLocaleDateString('fr-FR')}
                </div>
                <div className="table-cell" data-label="Montant">
                  {document.amount ? formatDZD(document.amount) : '-'}
                </div>
                <div className="table-cell" data-label="Statut">
                  <span 
                    className="status-badge"
                    style={{ 
                      backgroundColor: `${docStatus.color}20`, 
                      color: docStatus.color 
                    }}
                  >
                    {docStatus.label}
                  </span>
                </div>
                <div className="table-cell actions" data-label="Actions">
                  <button 
                    className="btn-icon"
                    onClick={() => handleView(document)}
                    title="Aperçu"
                  >
                    <Eye size={16} />
                  </button>
                  <button 
                    className="generate-pdf-button"
                    onClick={() => handleGeneratePDF(document)}
                    disabled={isGeneratingPDF}
                  >
                    <Download size={16} />
                    Générer PDF
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="documents-grid">
          {filteredDocuments.map(document => {
            // Safely get document type or use default if type doesn't exist
            const docType = documentTypes[document.type] || documentTypes.default;
            // Safely get status or use a default
            const docStatus = statusConfig[document.status] || { label: 'Statut', color: '#64748b' };
            const IconComponent = docType.icon;

            return (
              <div key={document.id} className="document-card">
                <div className="card-header">
                  <div className="document-type-info">
                    <div className="type-icon" style={{ backgroundColor: docType.color }}>
                      <IconComponent size={20} />
                    </div>
                    <div className="type-details">
                      <span className="document-number">{document.number}</span>
                      <span className="document-type">{docType.label}</span>
                    </div>
                  </div>
                  <div className="card-actions">
                    <button 
                      className="btn-icon"
                      onClick={() => handleView(document)}
                      title="Aperçu"
                    >
                      <Eye size={16} />
                    </button>
                  </div>
                </div>
                
                <div className="card-content">
                  <h3 className="document-title">{document.title}</h3>
                  
                  <div className="document-details">
                    <div className="detail-row">
                      <User size={14} />
                      <span>{document.client}</span>
                    </div>
                    <div className="detail-row">
                      <Calendar size={14} />
                      <span>{new Date(document.date).toLocaleDateString('fr-FR')}</span>
                    </div>
                    {document.amount && (
                      <div className="detail-row">
                        <DollarSign size={14} />
                        <span>{formatDZD(document.amount)}</span>
                      </div>
                    )}
                    {document.items && document.items.length > 0 && (
                      <div className="detail-row products-row">
                        <Tag size={14} />
                        <div className="card-product-names">
                          {document.items.slice(0, 2).map((item, index) => (
                            <span key={item.id} className="card-product-name">
                              {item.name}
                            </span>
                          ))}
                          {document.items.length > 2 && (
                            <span className="more-products">+{document.items.length - 2} autres</span>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="detail-row">
                      <Tag size={14} />
                      <span>{document.category}</span>
                    </div>
                  </div>
                </div>
                
                <div className="card-footer">
                  <span 
                    className="status-badge"
                    style={{ 
                      backgroundColor: `${docStatus.color}20`, 
                      color: docStatus.color 
                    }}
                  >
                    {docStatus.label}
                  </span>
                  <button 
                    className="generate-pdf-button"
                    onClick={() => handleGeneratePDF(document)}
                    disabled={isGeneratingPDF}
                  >
                    <Download size={16} />
                    Générer PDF
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filteredDocuments.length === 0 && (
        <div className="no-documents">
          <Archive size={48} />
          <h3>Aucun document trouvé</h3>
          <p>Essayez de modifier vos critères de recherche</p>
        </div>
      )}

      {/* Document Preview Modal */}
      {showPreviewModal && previewDocument && (
        <div className="document-preview-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Aperçu: {previewDocument.title || previewDocument.number}</h3>
              <button className="close-btn" onClick={() => setShowPreviewModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="document-preview-details">
                <div className="preview-detail">
                  <strong>Type:</strong> {(documentTypes[previewDocument.type] || documentTypes.default).label}
                </div>
                <div className="preview-detail">
                  <strong>Numéro:</strong> {previewDocument.number}
                </div>
                <div className="preview-detail">
                  <strong>Date:</strong> {new Date(previewDocument.date).toLocaleDateString('fr-FR')}
                </div>
                <div className="preview-detail">
                  <strong>Client:</strong> {previewDocument.client}
                </div>
                {previewDocument.amount && (
                  <div className="preview-detail">
                    <strong>Montant:</strong> {formatDZD(previewDocument.amount)}
                  </div>
                )}
              </div>
              {previewDocument.items && previewDocument.items.length > 0 && (
                <div className="document-preview-items">
                  <h4>Produits/Services</h4>
                  <div className="preview-items-list">
                    {previewDocument.items.map((item, index) => (
                      <div key={index} className="preview-item">
                        <span className="item-name">{item.name}</span>
                        <span className="item-quantity">x{item.quantity}</span>
                        <span className="item-price">{formatDZD(item.price)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPreviewModal(false)}>
                Fermer
              </button>
              <button 
                className="generate-pdf-button"
                onClick={() => handleGeneratePDF(previewDocument)}
                disabled={isGeneratingPDF}
              >
                <Download size={16} />
                Générer PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Archives; 